import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import type { ApprovalRequestStatus } from '@/api/hooks/useApprovalRequests';
import {
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  Undo2,
  TimerOff,
  Ban,
} from 'lucide-react';

/**
 * One place that decides how a status looks, so the list, the detail header and
 * the pending badge cannot drift apart.
 *
 * CONFLICTED and APPLY_FAILED are deliberately styled as loud as REJECTED: they
 * mean an approved change did NOT land, which is the state most likely to be
 * silently ignored and the one an operator most needs to act on.
 */
const STYLES: Record<
  ApprovalRequestStatus,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string; Icon: typeof Clock }
> = {
  PENDING: { variant: 'outline', className: 'border-amber-500 text-amber-600 dark:text-amber-400', Icon: Clock },
  APPROVED: { variant: 'outline', className: 'border-sky-500 text-sky-600 dark:text-sky-400', Icon: CheckCircle2 },
  APPLIED: { variant: 'outline', className: 'border-emerald-500 text-emerald-600 dark:text-emerald-400', Icon: CheckCircle2 },
  REJECTED: { variant: 'destructive', className: '', Icon: XCircle },
  WITHDRAWN: { variant: 'secondary', className: '', Icon: Undo2 },
  EXPIRED: { variant: 'secondary', className: '', Icon: TimerOff },
  CONFLICTED: { variant: 'destructive', className: '', Icon: AlertTriangle },
  APPLY_FAILED: { variant: 'destructive', className: '', Icon: Ban },
};

export function ApprovalStatusBadge({ status }: { status: ApprovalRequestStatus }) {
  const { t } = useTranslation('approvals');
  const style = STYLES[status] ?? STYLES.PENDING;
  const Icon = style.Icon;

  return (
    <Badge variant={style.variant} className={`gap-1 ${style.className}`}>
      <Icon className="h-3 w-3" />
      {t(`status.${status}`, { defaultValue: status })}
    </Badge>
  );
}
